import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const STORAGE_KEY = "dcb_scroll_positions";
const MAX_RESTORE_ATTEMPTS = 30;
const RESTORE_INTERVAL_MS = 50;

function readPositions() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function writePositions(map) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota errors */
  }
}

function savePosition(key, y) {
  const positions = readPositions();
  positions[key] = y;
  writePositions(positions);
}

if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

/**
 * Restores window.scrollY when the user navigates back/forward (POP) and
 * scrolls to top for fresh navigations. The current page's scroll position
 * is saved synchronously the moment the user clicks ANY internal link
 * (capture-phase click handler) — this avoids race conditions where the
 * browser auto-clamps scrollY when the next page mounts.
 *
 * Mount once inside <BrowserRouter>.
 */
export default function ScrollRestoration() {
  const location = useLocation();
  const navType = useNavigationType();
  const key = location.pathname + location.search;
  const currentKeyRef = useRef(key);

  useEffect(() => { currentKeyRef.current = key; }, [key]);

  // Capture-phase click handler: save scroll position synchronously the
  // moment any internal link is clicked, before React Router updates the
  // history and before the browser starts changing the DOM/scroll position.
  useEffect(() => {
    const onClick = (e) => {
      // Walk up the DOM to find an <a> ancestor of the click target
      let el = e.target;
      while (el && el !== document.body) {
        if (el.tagName === "A" && el.getAttribute("href")) {
          const href = el.getAttribute("href");
          // Internal links only (not external, mailto:, tel:, # anchors, target=_blank)
          if (
            href.startsWith("/") &&
            !href.startsWith("//") &&
            el.target !== "_blank" &&
            !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
          ) {
            savePosition(currentKeyRef.current, window.scrollY);
          }
          return;
        }
        el = el.parentElement;
      }
    };
    // Also save on pagehide (in case user types a new URL or closes the tab)
    const onPageHide = () => savePosition(currentKeyRef.current, window.scrollY);

    document.addEventListener("click", onClick, true);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  // Also save on browser back/forward (POP) before the new page renders.
  // This lets the user navigate A → B → A → C → A and have each list
  // position preserved independently.
  useEffect(() => {
    const onPopState = () => {
      // Save position of the page we're LEAVING (currentKeyRef still holds it)
      savePosition(currentKeyRef.current, window.scrollY);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // On every route change, decide whether to restore or scroll to top
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    if (navType === "POP") {
      const saved = readPositions()[key];
      if (typeof saved === "number" && saved > 0) {
        let attempts = 0;
        const tryRestore = () => {
          if (cancelled) return;
          window.scrollTo({ top: saved, left: 0, behavior: "auto" });
          if (Math.abs(window.scrollY - saved) <= 2 || attempts >= MAX_RESTORE_ATTEMPTS) return;
          attempts += 1;
          timer = setTimeout(tryRestore, RESTORE_INTERVAL_MS);
        };
        requestAnimationFrame(tryRestore);
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [key, navType]);

  return null;
}
